import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  GetSampleBottles,
  DeleteSampleBottle,
  AddSampleBottle,
  UpdateSampleBottle,
} from "../../../services/SamplesMaintenanceService"; // Import the necessary service functions
import CommonLoader from "../../common/CommonLoader";
import ViewSampleBottle from "./ViewSampleBottle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import AddSampleBottleDialog from "./AddSampleBottleDialog"; // Import the dialog component for adding new sample bottles
import { NotificationManager } from "react-notifications";

const SampleBottlesTable = () => {
  const columns = [
    "Sample Bottle Name",
    "Description",
    "Inv Quantity",
    "Active",
    "Actions",
  ];
  const columnHeaders = ["sampleBottleName", "description", "invQuantity"];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [sampleBottlesData, setSampleBottlesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSampleBottle, setSelectedSampleBottle] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);
  const [isNewSampleDialogOpen, setIsNewSampleDialogOpen] = useState(false);
  const [selectedItemToEdit, setSelectedItemToEdit] = useState(null);

  useEffect(() => {
    fetchSampleBottles();
  }, []);

  const fetchSampleBottles = async () => {
    try {
      setLoading(true);
      const response = await GetSampleBottles();
      setSampleBottlesData(response.results);
      setFilteredData(response.results);
    } catch (error) {
      console.error("Error fetching sample bottles:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filteredResults = sampleBottlesData.filter((sampleBottle) =>
      columnHeaders.some((header) => {
        const propertyValue = String(sampleBottle[header]).toLowerCase(); // Convert to string
        return propertyValue.includes(searchTerm.toLowerCase());
      })
    );

    setFilteredData(filteredResults);
  }, [searchTerm, sampleBottlesData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderActions = (sampleBottle) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(sampleBottle)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(sampleBottle)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(sampleBottle)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  const handleDeleteClick = (sampleBottle) => {
    setSelectedItemToDelete(sampleBottle);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteSampleBottle(
        selectedItemToDelete.sampleBottleName
      );
      if (response.results === true) {
        setIsDeleteConfirmationOpen(false);
        const updatedSampleBottles = sampleBottlesData.filter(
          (item) =>
            item.sampleBottleName !== selectedItemToDelete.sampleBottleName
        );
        setSampleBottlesData(updatedSampleBottles);
        setFilteredData(updatedSampleBottles);
        NotificationManager.success("Sample bottle deleted successfully");
      } else {
        setIsDeleteConfirmationOpen(false);
        NotificationManager.error("Error deleting sample bottle");
      }
    } catch (error) {
      NotificationManager.error("Error deleting sample bottle");
      setIsDeleteConfirmationOpen(false);
    }
  };

  const handleViewClick = (sampleBottle) => {
    setSelectedSampleBottle(sampleBottle);
  };

  const handleEditClick = (sampleBottle) => {
    setSelectedItemToEdit(sampleBottle);
    setIsNewSampleDialogOpen(true);
  };
  const handleBackClick = () => {
    setSelectedSampleBottle(null);
    setSearchTerm("");
  };

  const handleNewSampleDialogClose = () => {
    setIsNewSampleDialogOpen(false); // Close the new sample dialog
  };

  const handleNewSampleDialogAdd = async (sampleBottleData) => {
    try {
      // Call the service function to add the new sample bottle
      await AddSampleBottle(sampleBottleData);
      // Close the dialog
      NotificationManager.success("Sample bottle added successfully");
      setIsNewSampleDialogOpen(false);
      setSelectedItemToEdit(null);
      // Fetch the updated sample bottles list
      fetchSampleBottles();
    } catch (error) {
      NotificationManager.error("Error adding new sample bottle:", error);
    }
  };

  const handleNewSampleDialogEdit = async (sampleBottleData) => {
    try {
      // Call the service function to add the new sample bottle
      await UpdateSampleBottle(sampleBottleData);
      NotificationManager.success("Sample bottle updated successfully");
      setSelectedItemToEdit(null);
      setIsNewSampleDialogOpen(false);
      // Fetch the updated sample bottles list
      fetchSampleBottles();
    } catch (error) {
      NotificationManager.error("Error updating new sample bottle:", error);
    }
  };
  return (
    <>
    <span className="title">Test Sample Bottles </span>
      {selectedSampleBottle ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewSampleBottle sampleBottle={selectedSampleBottle} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setIsNewSampleDialogOpen(true)}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer>
              <Table sx={{ minWidth: 400 }} size="small">
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((sampleBottle, index) => (
                      <TableRow
                        key={sampleBottle.sampleBottleName}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                        }}
                      >
                        {columnHeaders.map((column) => (
                          <TableCell key={column}>
                            {sampleBottle[column]}
                          </TableCell>
                        ))}
                        <TableCell>
                          {sampleBottle.isActive === false ? "Inactive" : "Active"}
                        </TableCell>
                        <TableCell>{renderActions(sampleBottle)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />

          {/* Render the delete confirmation popup */}
          <DeleteConfirmation
            isOpen={isDeleteConfirmationOpen}
            onClose={handleDeleteConfirmationClose}
            onConfirm={handleDeleteConfirmationConfirm}
            title="Delete Confirmation"
            message={`Are you sure you want to delete the ${selectedItemToDelete?.sampleBottleName}?`}
            color="red"
          />

          <AddSampleBottleDialog
            open={isNewSampleDialogOpen}
            onClose={handleNewSampleDialogClose}
            onAdd={handleNewSampleDialogAdd}
            onEdit={handleNewSampleDialogEdit}
            selectedItemToEdit={selectedItemToEdit}
          />
        </>
      )}
    </>
  );
};

export default SampleBottlesTable;
